import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useLocales } from '@web-config-app/core-react';
import { JsonFormsUISchemaRegistryEntry, findUISchema } from '@jsonforms/core';
import { EntityFormArrayControlBaseProps } from '../../types/controls';
import { createDefaultValueForSchema } from '../../utils/create-default-value-for-schema/create-default-value-for-schema';
import { useLocalizedArrayOperations } from '../use-localized-array-operations/use-localized-array-operations';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';

export interface UseArrayActionsProps
  extends Pick<
    EntityFormArrayControlBaseProps,
    | 'uischema'
    | 'uischemas'
    | 'schema'
    | 'path'
    | 'rootSchema'
    | 'moveUp'
    | 'moveDown'
    | 'removeItems'
    | 'localized'
  > {
  data: Array<string | undefined> | undefined;
  formPath?: string;
  currentLocale?: string;
}

export const useArrayPrimitiveActions = ({
  uischema,
  uischemas,
  schema,
  path,
  rootSchema,
  moveDown,
  moveUp,
  removeItems,
  data,
  formPath,
  localized,
  currentLocale,
}: UseArrayActionsProps) => {
  /**
   * formPathCache, cacheKeys and dataCache are used only in the context when the control is rendering an array of
   * RichTextInputControl items. RichTextInputControl has some quirks around re-rendering with new content and
   * require passing a key to indicate that it's time to recreate its editor.
   */
  const formPathCache = useRef<string | undefined>(formPath);
  const localeCache = useRef<string | undefined>(currentLocale);
  const [cacheKeys, setCacheKeys] = useState<
    (string | undefined)[] | undefined
  >();
  const [dataCache, setDataCache] = useState<(string | undefined)[]>();

  const { entity } = useEntityDetailsProps();

  const { locales } = useLocales();
  const isLocalizable =
    localized && locales && locales.length > 1 && entity.localizable;

  // localized array operations will only be used if isLocalizable is true
  const {
    handleLocalizedMoveUp,
    handleLocalizedMoveDown,
    handleLocalizedRemoveItem,
    addLocalizedArrayItem,
    handleLocalizedMoveToPosition,
  } = useLocalizedArrayOperations({
    path,
    formPath,
    placeholderValue: '',
  });

  const childUiSchema = useMemo(
    () =>
      findUISchema(
        uischemas as JsonFormsUISchemaRegistryEntry[],
        schema,
        uischema.scope,
        path,
        undefined,
        uischema,
        rootSchema,
      ),
    [uischemas, schema, path, uischema, rootSchema],
  );

  const handleCreateDefaultValue = useCallback(() => {
    const defaultValue = createDefaultValueForSchema(schema);
    if (isLocalizable) {
      addLocalizedArrayItem();
    }
    return defaultValue;
  }, [addLocalizedArrayItem, isLocalizable, schema]);

  const handleRemoveItem = useCallback(
    (itemPath: string, index: number) => () => {
      removeItems(itemPath, [index])();

      if (isLocalizable) {
        handleLocalizedRemoveItem({ index });
      }

      setCacheKeys([]);
    },
    [removeItems, isLocalizable, handleLocalizedRemoveItem],
  );

  const handleMoveUp = useCallback(
    (itemPath: string, index: number) => () => {
      moveUp(itemPath, index)();

      if (isLocalizable) {
        handleLocalizedMoveUp({ index });
      }

      setCacheKeys([]);
    },
    [moveUp, isLocalizable, handleLocalizedMoveUp],
  );

  const handleMoveDown = useCallback(
    (itemPath: string, index: number) => () => {
      moveDown(itemPath, index)();

      if (isLocalizable) {
        handleLocalizedMoveDown({ index });
      }

      setCacheKeys([]);
    },
    [moveDown, isLocalizable, handleLocalizedMoveDown],
  );

  /**
   * `getArrayItemCacheKey`, like `cacheKeys` and `dataCache` are used only in the context of an array of
   * RichTextInputControl items
   */
  const getArrayItemCacheKey = useCallback(
    (index: number) => {
      if (!Array.isArray(cacheKeys)) return null;
      // Include currentLocale in the cache key to ensure it changes when locale changes
      const key = `${cacheKeys[index]}.${currentLocale ?? ''}`;
      return key;
    },
    [cacheKeys, currentLocale],
  );

  useEffect(() => {
    /**
     * Our last bit of special handling for RichTextInputControl arrays, this useEffect just handles
     * recalculating the keys passed to the individual controls when required.
     */
    if (schema.format === 'rich-text' && Array.isArray(data)) {
      let refreshKeys = false;
      if (!Array.isArray(dataCache)) {
        /**
         * Initialize the data cache
         */
        setDataCache(data);
        refreshKeys = true;
      } else if (data.length === dataCache.length) {
        /**
         * If the data changed but we have the same number of items, loop through them and
         * check if the order of two items has flipped. If so, the user re-ordered the items.
         *
         * Otherwise, we can assume they simply edited the content in one of the fields.
         */

        data.forEach((item, index) => {
          if (
            typeof item === 'string' &&
            item !== dataCache[index] &&
            (item === dataCache[index - 1] || item === dataCache[index + 1])
          ) {
            refreshKeys = true;
          }
        });
      } else {
        /**
         * An item was added or removed
         */
        refreshKeys = true;
      }

      // Check for form path or locale changes
      const isFormPathChanged = formPathCache.current !== formPath;
      const isLocaleChanged = localeCache.current !== currentLocale;

      if (refreshKeys || isFormPathChanged || isLocaleChanged) {
        setCacheKeys(data.map(() => crypto.randomUUID()));
        formPathCache.current = formPath;
        localeCache.current = currentLocale;
      }

      setDataCache(data);
    }
  }, [data, dataCache, formPath, currentLocale, schema.format]);

  const clearCacheKeys = useCallback(() => {
    setCacheKeys([]);
    // Also clear the data cache to force regeneration of cache keys
    // This is needed when items move more than one position (drag-and-drop)
    // because the adjacent-position detection logic won't catch it
    setDataCache([]);
  }, []);

  return {
    childUiSchema,
    handleCreateDefaultValue,
    handleMoveUp,
    handleMoveDown,
    handleRemoveItem,
    getArrayItemCacheKey,
    clearCacheKeys,
    handleLocalizedMoveToPosition,
    isLocalizable,
  };
};
